import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isRTL } from '../utils/translations';

const HorizontalProductRow = ({ title, subtitle, kicker, products, rowCount = 1, onAddToCart, viewAllLink = '/category/all' }) => {
    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftState, setScrollLeftState] = useState(0);

    const scroll = (direction) => {
        if (!scrollRef.current) return;
        const container = scrollRef.current;
        const offset = (direction === 'left' ? -420 : 420) * (isRTL() ? -1 : 1);
        container.scrollBy({ left: offset, behavior: 'smooth' });
    };

    const handleMouseDown = (e) => {
        if (!scrollRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeftState(scrollRef.current.scrollLeft);
    };

    const handleMouseLeaveOrUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !scrollRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        scrollRef.current.scrollLeft = scrollLeftState - walk;
    };

    if (!products || products.length === 0) return null;

    const rtl = isRTL();
    const hasTitleHeader = Boolean(title || subtitle || kicker);

    return (
        <section className="section container horizontal-product-section">
            {hasTitleHeader && (
                <div
                    className="section-header horizontal-product-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap',
                        marginBottom: '14px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid rgba(15, 23, 42, 0.06)'
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            {kicker ? (
                                <span
                                    className="section-kicker"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        fontWeight: '800',
                                        color: '#e02424',
                                        background: 'rgba(224, 36, 36, 0.08)',
                                        border: '1px solid rgba(224, 36, 36, 0.18)',
                                        padding: '3px 10px',
                                        borderRadius: '999px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                        margin: 0
                                    }}
                                >
                                    <span style={{ fontSize: '8px' }}>●</span>
                                    <span>{kicker}</span>
                                </span>
                            ) : null}
                            {title ? (
                                <h2
                                    className="section-title horizontal-section-title"
                                    style={{ margin: 0, fontSize: '21px', fontWeight: '800', letterSpacing: '-0.01em' }}
                                >
                                    {title}
                                </h2>
                            ) : null}
                        </div>
                        {subtitle ? (
                            <p
                                className="section-subtitle"
                                style={{ margin: '2px 0 0', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}
                            >
                                {subtitle}
                            </p>
                        ) : null}
                    </div>

                    {/* Desktop & Tablet Carousel Movement Buttons + View All Pill */}
                    <div className="horizontal-scroll-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {viewAllLink && (
                            <Link
                                to={viewAllLink}
                                className="horizontal-view-all-link"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '7px 14px',
                                    borderRadius: '999px',
                                    fontSize: '12.5px',
                                    fontWeight: '700',
                                    color: 'var(--text, #111827)',
                                    background: 'rgba(15, 23, 42, 0.04)',
                                    border: '1px solid rgba(15, 23, 42, 0.09)',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <span>{rtl ? 'عرض الكل' : 'View All'}</span>
                                <span style={{ fontSize: '13px', lineHeight: 1 }}>{rtl ? '←' : '→'}</span>
                            </Link>
                        )}
                        <button
                            type="button"
                            className="btn-icon horizontal-scroll-btn"
                            onClick={() => scroll('left')}
                            aria-label="تحريك لليسار"
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                border: '1px solid rgba(15, 23, 42, 0.12)',
                                background: 'var(--lux-card-bg, #ffffff)',
                                color: 'var(--text, #111827)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            type="button"
                            className="btn-icon horizontal-scroll-btn"
                            onClick={() => scroll('right')}
                            aria-label="تحريك لليمين"
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                border: '1px solid rgba(15, 23, 42, 0.12)',
                                background: 'var(--lux-card-bg, #ffffff)',
                                color: 'var(--text, #111827)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}

            <div 
                className="horizontal-product-row-scroll" 
                ref={scrollRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeaveOrUp}
                onMouseUp={handleMouseLeaveOrUp}
                onMouseMove={handleMouseMove}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    gap: '14px',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    padding: '4px 2px 10px',
                    width: '100%',
                    boxSizing: 'border-box',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: isDragging ? 'none' : 'auto',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {products.map(product => (
                    <div 
                        className="horizontal-product-item" 
                        key={product.id}
                        style={{
                            flex: products.length <= 6 ? '1 1 215px' : '0 0 220px',
                            width: products.length <= 6 ? 'auto' : '220px',
                            minWidth: '190px',
                            maxWidth: products.length <= 6 ? '310px' : '240px',
                            boxSizing: 'border-box'
                        }}
                    >
                        <ProductCard product={product} onAddToCart={onAddToCart} />
                    </div>
                ))}
            </div>
        </section>
    );
};

export default HorizontalProductRow;

